import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export const BlogShowcase = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/blog/posts?limit=3`)
      .then((res) => setPosts(res.data.posts || []))
      .catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  return (
    <section className="py-20 lg:py-28 bg-[#080808] relative overflow-hidden" data-testid="blog-showcase">
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12"
        >
          <div>
            <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">From the Foundry</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}>
              LATEST FROM THE BLOG
            </h2>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors group"
            data-testid="blog-showcase-view-all"
          >
            View all articles
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {posts.map((post, i) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.1 }}
            >
              <Link
                to={`/blog/${post.slug}`}
                className="group block bg-[#111111] border border-zinc-800 hover:border-[#D4AF37]/50 transition-colors rounded-sm overflow-hidden h-full"
                data-testid={`blog-showcase-card-${post.slug}`}
              >
                <div className="aspect-video overflow-hidden bg-zinc-900">
                  {post.hero_image_url ? (
                    <img
                      src={post.hero_image_url}
                      alt={post.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-9 w-9 text-zinc-700" />
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <p className="text-[11px] text-zinc-500 tracking-wider uppercase mb-2">{formatDate(post.published_at)}</p>
                  <h3 className="text-lg text-white group-hover:text-[#D4AF37] transition-colors leading-snug mb-2" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.03em" }}>
                    {post.title}
                  </h3>
                  <p className="text-sm text-zinc-500 line-clamp-2">{post.excerpt || post.meta_description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogShowcase;
